import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const QuickProductForm = ({ onSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    category_id: '',
    price_b2c: '',
    price_b2b: '',
    cost_price: '',
    description: ''
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      // Handle different response formats
      const categoriesData = Array.isArray(response.data) 
        ? response.data 
        : response.data.categories || [];
      setCategories(categoriesData);
    } catch (error) {
      toast.error('שגיאה בטעינת קטגוריות');
      setCategories([]); // Set empty array on error
    }
  };

  const generateSKU = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({ ...prev, sku: `SKU-${timestamp}-${random}` }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-fill B2B price if not set
    if (name === 'price_b2c' && !formData.price_b2b) {
      setFormData(prev => ({
        ...prev,
        price_b2b: value
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sku || !formData.name || !formData.category_id || !formData.price_b2c) {
      toast.error('יש למלא את כל השדות החובה');
      return;
    }

    try {
      setLoading(true);

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          submitData.append(key, formData[key]);
        }
      });

      if (image) {
        submitData.append('image', image);
      }

      const response = await axios.post('/api/products/quick-create', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      onSuccess(response.data.product);
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה ביצירת המוצר');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* SKU */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          SKU *
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            name="sku"
            value={formData.sku}
            onChange={handleInputChange}
            required
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="SKU-12345"
          />
          <button
            type="button"
            onClick={generateSKU}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
          >
            יצירה אוטומטית
          </button>
        </div>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          שם המוצר *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="שם המוצר"
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          קטגוריה *
        </label>
        <select
          name="category_id"
          value={formData.category_id}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">בחר קטגוריה</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Prices */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מחיר B2C *
          </label>
          <input
            type="number"
            step="0.01"
            name="price_b2c"
            value={formData.price_b2c}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מחיר B2B
          </label>
          <input
            type="number"
            step="0.01"
            name="price_b2b"
            value={formData.price_b2b}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            מחיר עלות
          </label>
          <input
            type="number"
            step="0.01"
            name="cost_price"
            value={formData.cost_price}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          תמונה (אופציונלי)
        </label>
        <div className="flex items-center gap-4">
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="w-20 h-20 object-cover rounded border border-gray-300"
            />
          )}
          <label className="flex-1 cursor-pointer">
            <div className="px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-500 transition-colors">
              <span className="text-sm text-gray-600">
                {image ? image.name : 'לחץ לבחירת תמונה או דלג'}
              </span>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          תיאור (אופציונלי)
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="תיאור קצר של המוצר"
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      >
        {loading ? 'יוצר מוצר...' : 'יצירת מוצר והמשך'}
      </button>

      <p className="text-xs text-gray-500 text-center">
        * לאחר יצירת המוצר תוכל להוסיף לו מלאי
      </p>
    </form>
  );
};

export default QuickProductForm;
